import {
  ApiClient,
  ApiError,
  UserSession,
  localStorageTokenStore,
  type AuthResponse,
  type ClaimPreview,
  type MeResponse,
  type TelegramAuthResponse,
  type TelegramPublicConfig,
} from '@pos/api-client';
import { toast } from '@pos/ui';
import { create } from 'zustand';

export const API_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:3000/api';
const BUSINESS_KEY = 'pos.owner.tokens.business';

interface AuthState {
  me: MeResponse | null;
  businessId: string | null;
  status: 'loading' | 'signed-out' | 'signed-in';
}

export const useAuth = create<AuthState>(() => ({ me: null, businessId: null, status: 'loading' }));

export const session = new UserSession(API_URL, localStorageTokenStore('pos.owner.tokens'), () =>
  useAuth.setState({ me: null, status: 'signed-out' }),
);

export const api = new ApiClient({
  baseUrl: API_URL,
  auth: session,
  headers: () => ({
    'X-Business-Id': useAuth.getState().businessId,
    // Identifies this client so the server can check the `owner_pwa` plan
    // feature. Not a security boundary — the same person can always reach the
    // same endpoints from the register's admin — it is how a plan that does not
    // include the phone app is enforced.
    'X-Client': 'owner-pwa',
  }),
});

function readBusiness(): string | null {
  try {
    return localStorage.getItem(BUSINESS_KEY);
  } catch {
    return null;
  }
}

export function selectBusiness(id: string): void {
  try {
    localStorage.setItem(BUSINESS_KEY, id);
  } catch {
    // ignore
  }
  useAuth.setState({ businessId: id });
}

function applyMe(me: MeResponse): void {
  const saved = readBusiness();
  const businessId =
    me.memberships.find((m) => m.businessId === saved)?.businessId ??
    me.memberships[0]?.businessId ??
    null;
  useAuth.setState({ me, businessId, status: 'signed-in' });
}

// A person blocked by the platform is signed out on their next request, with
// the reason in words, instead of every page failing one by one.
const request = api.request.bind(api);
api.request = (async (...args: Parameters<ApiClient['request']>) => {
  try {
    return await request(...args);
  } catch (e) {
    if (
      e instanceof ApiError &&
      e.code === 'ACCOUNT_BLOCKED' &&
      useAuth.getState().status === 'signed-in'
    ) {
      await session.signOut();
      useAuth.setState({ me: null, status: 'signed-out' });
      toast.error(e.message);
    }
    throw e;
  }
}) as ApiClient['request'];

export async function restore(): Promise<void> {
  if (!session.isSignedIn()) {
    useAuth.setState({ status: 'signed-out' });
    return;
  }
  try {
    applyMe(await api.get<MeResponse>('/auth/me'));
  } catch (e) {
    // Offline: keep the session; the app shows a retry screen until the server answers.
    if (e instanceof ApiError && e.isNetwork) useAuth.setState({ status: 'signed-in' });
    else useAuth.setState({ me: null, status: 'signed-out' });
  }
}

export async function signIn(login: string, password: string): Promise<MeResponse> {
  const res = await api.post<AuthResponse>('/auth/login', { login, password });
  applySignIn(res);
  return res.me;
}

/** Stores the tokens of a fresh sign-in or sign-up and opens the (new) business. */
export function applySignIn(res: AuthResponse): void {
  session.set(res.tokens);
  applyMe(res.me);
}

export async function signOut(): Promise<void> {
  await session.signOut();
  useAuth.setState({ me: null, status: 'signed-out' });
}

export function loginErrorText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.isNetwork) return 'Нет связи с сервером. Проверьте интернет.';
    if (e.code === 'INVALID_CREDENTIALS') return 'Неверный логин или пароль';
    if (e.code === 'RATE_LIMITED') return 'Слишком много попыток. Подождите минуту.';
    return e.message;
  }
  return 'Не удалось войти';
}

// ───────────────────────── Telegram Login ─────────────────────────
// The browser only carries Telegram's id_token and our nonce to the server;
// the server verifies them and answers with the existing session.

export const telegramConfig = () => api.get<TelegramPublicConfig>('/auth/telegram/config');
export const telegramNonce = async () =>
  (await api.post<{ nonce: string }>('/auth/telegram/nonce')).nonce;

/** Signed in → the app opens; unknown person → the registration step with its token. */
export async function signInWithTelegram(
  idToken: string,
  nonce: string,
): Promise<Extract<TelegramAuthResponse, { status: 'registration_required' }> | null> {
  const res = await api.post<TelegramAuthResponse>('/auth/telegram', { idToken, nonce });
  if (res.status === 'signed_in') {
    applySignIn(res);
    if (res.phoneConflict)
      toast.warning('Номер телефона из Telegram уже у другого аккаунта — он не привязан');
    return null;
  }
  return res;
}

export async function completeTelegramSignUp(
  registrationToken: string,
  businessName: string,
  ownerName: string,
): Promise<void> {
  const res = await api.post<TelegramAuthResponse>('/auth/telegram/register', {
    registrationToken,
    businessName,
    ownerName,
  });
  if (res.status === 'signed_in') {
    applySignIn(res);
    if (res.phoneConflict)
      toast.warning('Номер телефона из Telegram уже у другого аккаунта — он не привязан');
  }
}

export const previewClaim = (token: string) =>
  api.get<ClaimPreview>(`/auth/telegram/claim/${encodeURIComponent(token)}`);

export async function claimWithTelegram(
  claimToken: string,
  idToken: string,
  nonce: string,
): Promise<void> {
  const res = await api.post<TelegramAuthResponse>('/auth/telegram/claim', {
    claimToken,
    idToken,
    nonce,
  });
  if (res.status === 'signed_in') applySignIn(res);
}

/** The signed-in person attaches Telegram (older password accounts). */
export async function linkTelegram(idToken: string, nonce: string): Promise<void> {
  const me = await api.post<MeResponse & { phoneConflict?: boolean }>('/auth/telegram/link', {
    idToken,
    nonce,
  });
  applyMe(me);
  if (me.phoneConflict)
    toast.warning('Номер телефона из Telegram уже у другого аккаунта — он не привязан');
}

export async function refreshMe(): Promise<void> {
  applyMe(await api.get<MeResponse>('/auth/me'));
}

/** Plain words for every Telegram answer the server can give. */
export function telegramErrorText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.isNetwork) return 'Нет связи с сервером. Проверьте интернет.';
    switch (e.code) {
      case 'TELEGRAM_EXPIRED':
        return 'Время входа истекло. Нажмите «Войти через Telegram» ещё раз.';
      case 'TELEGRAM_INVALID':
        return 'Telegram не подтвердил вход. Попробуйте ещё раз.';
      case 'TELEGRAM_NOT_CONFIGURED':
        return 'Вход через Telegram ещё не настроен. Напишите в поддержку.';
      case 'TELEGRAM_ALREADY_LINKED':
        return 'Этот Telegram уже привязан к другому аккаунту Gertsak.';
      case 'ACCOUNT_HAS_TELEGRAM':
        return 'К этому аккаунту уже привязан другой Telegram.';
      case 'CLAIM_EXPIRED':
        return 'Ссылка устарела или уже использована. Попросите новую.';
      case 'ACCOUNT_BLOCKED':
        return e.message;
      case 'RATE_LIMITED':
        return 'Слишком много попыток. Подождите минуту.';
      default:
        return e.status >= 500 ? 'Сервер не ответил. Попробуйте ещё раз.' : e.message;
    }
  }
  return 'Не получилось войти. Попробуйте ещё раз.';
}

/** The person's own phone, typed by them: kept unverified. */
export async function setMyPhone(phone: string): Promise<void> {
  applyMe(await api.patch<MeResponse>('/auth/me/phone', { phone }));
}
